import { isDefined } from "@clipboard-health/util-ts";
import {
  ExtraWorkedTimeRequestStatus,
  ExtraWorkedTimeRequestStatusUpdater,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";

import {
  type ExtraWorkedTimeRequestType,
  type ShiftType,
} from "../../../api/usePaginatedWorkerShifts";
import { type ShiftHistoryListItemVariant } from "./types";

interface ExtraWorkedTimeRequestData {
  attributes: ExtraWorkedTimeRequestType["attributes"];
  meta: ExtraWorkedTimeRequestType["meta"];
}

interface RequestWithId extends ExtraWorkedTimeRequestData {
  id: string;
}

/**
 * Returns the latest actionable ExtraWorkedTimeRequest for a shift — the most recent
 * request that is either PENDING or DECLINED. Approved requests and requests where
 * the user accepted original times (meta.isOutcomeAcknowledged) are fully resolved.
 */
export function getRelevantExtraWorkedTimeRequest(
  shift: ShiftType,
  extraWorkedTimeRequestMap: Map<string, ExtraWorkedTimeRequestData>
): ExtraWorkedTimeRequestType["attributes"] | undefined {
  const extraWorkedTimeRequestIds = shift.relationships?.extraWorkedTimeRequest?.data;
  if (!isDefined(extraWorkedTimeRequestIds) || extraWorkedTimeRequestIds.length === 0) {
    return undefined;
  }

  const requests: RequestWithId[] = extraWorkedTimeRequestIds
    .map(({ id }) => {
      const data = extraWorkedTimeRequestMap.get(id);
      if (!isDefined(data)) {
        return undefined;
      }

      return { id, ...data };
    })
    .filter((entry): entry is RequestWithId => isDefined(entry));

  if (requests.length === 0) {
    return undefined;
  }

  const [latest] = requests.toSorted((a, b) => {
    const aTime = a.attributes?.createdAt ?? "";
    const bTime = b.attributes?.createdAt ?? "";
    return bTime.localeCompare(aTime);
  });

  if (!isDefined(latest) || !isDefined(latest.attributes)) {
    return undefined;
  }

  if (
    latest.attributes.status === ExtraWorkedTimeRequestStatus.APPROVED ||
    latest.meta?.isOutcomeAcknowledged
  ) {
    return undefined;
  }

  return latest.attributes;
}

export function getShiftHistoryListItemVariant(
  isVerified: boolean,
  relevantRequest: ExtraWorkedTimeRequestType["attributes"] | undefined
): ShiftHistoryListItemVariant {
  if (isDefined(relevantRequest)) {
    if (relevantRequest.status === ExtraWorkedTimeRequestStatus.PENDING) {
      return "pending";
    }

    if (relevantRequest.status === ExtraWorkedTimeRequestStatus.DECLINED) {
      if (relevantRequest.statusUpdatedBy === ExtraWorkedTimeRequestStatusUpdater.SYSTEM) {
        return "expired";
      }

      return "declined";
    }
  }

  if (!isVerified) {
    return "unverified";
  }

  return "verified";
}
